import {NodeSelection, TextSelection} from 'prosemirror-state';
import {moveCellForward} from 'prosemirror-tables';
import {findParentNode, findSelectedNodeOfType} from 'prosemirror-utils';

export const isParagraphEmpty = (node, schema) =>
    node.type === schema.nodes.paragraph &&
    node.textContent === "" &&
    node.childCount === 0 &&
    node.content.size === 0;

export const isNodeTypesSelected = (nodeTypes, selection) => {
    let $head = selection.$head;
    for (let d = $head.depth; d > 0; d--) {
        if (nodeTypes.includes($head.node(d).type)) {
            return true;
        }
    }
    return false;
}

export const isInGrid = state => isNodeTypesSelected([state.schema.nodes.grid], state.selection);

export const isInGridColumn = state => {
  const selection = state.selection;
  if (!selection || selection.empty || !selection.node) {
    return false;
  }

  return selection.node.type === state.schema.nodes.grid_column;
};

export const isInFormLabel = state => isNodeTypesSelected([state.schema.nodes.form_label], state.selection);

export const isInList = state => isNodeTypesSelected([state.schema.nodes.bullet_list, state.schema.nodes.ordered_list, state.schema.nodes.checkbox_list], state.selection);

export const isInCheckList = state => isNodeTypesSelected([state.schema.nodes.checkbox_list], state.selection);

export const isInBulletOrOrderedList = state => isNodeTypesSelected([state.schema.nodes.bullet_list, state.schema.nodes.ordered_list], state.selection);

export const isInHeading = state => isNodeTypesSelected([state.schema.nodes.heading], state.selection);

export const isInCardHeading = state => isNodeTypesSelected([state.schema.nodes.card_content_header], state.selection);

export const isInCardContent = state => isNodeTypesSelected([state.schema.nodes.card_content], state.selection);

export const isInCard = state => isNodeTypesSelected([state.schema.nodes.card], state.selection);

export const isInMessage = state => isNodeTypesSelected([state.schema.nodes.container_message], state.selection);

export const isInPageTitle = state => isNodeTypesSelected([state.schema.nodes.page_title], state.selection);

export const isInNotEmptyParagraphBlock = state => {
    let $head = state.selection.$head;
    for (let d = $head.depth; d > 0; d--) {
        if (state.schema.nodes.paragraph === $head.node(d).type) {
            return !isParagraphEmpty($head.node(d), state.schema)
        }
    }
    return false;
};
export const isImage = state => {
    const selection = state.selection;
    if (!selection || selection.empty || !selection.node) {
        return false;
    }

    return selection.node.type === state.schema.nodes.image;
};

export const isText = state => {
    const selection = state.selection;
    if (!selection || selection.empty || selection.node) {
        return false;
    }

    const slice = selection.content();
    const fragment = slice.content;
    const nodes = fragment.content;

    return nodes.some(node => node.content.size);
}

export const isLink = state => findLink(state) != null;

export const findLink = state => findMark(state, state.schema.marks.link);

export const findRelativeTime = state => findMark(state, state.schema.marks.relative_time);

const findMark = (state, markType) => {
    const {from} = state.selection;
    const nodeAfter = state.doc.resolve(from).nodeAfter;
    if (nodeAfter) {
        return nodeAfter.marks.find(mark => mark.type === markType);
    }

    return null;
}

export const goToNextColumn = direction => {
  return (state, dispatch) => {
    if (!isInGrid(state)) {
      return false;
    }
    let cell = findNextColumn(state.doc.resolve(findColumn(state).pos), direction);
    if (cell == null) return;
    if (dispatch) {
      let $cell = state.doc.resolve(cell);
      if ($cell.nodeAfter && $cell.nodeAfter.type === state.schema.nodes.grid_column) {
        dispatch(
            state.tr.setSelection(
                TextSelection.between($cell, moveCellForward($cell))).
            scrollIntoView(),
        );
      } else {
        return null;
      }
    }
    return true;
  };
}

export const findAccordion = state => {
  return findSelectedNodeOfType(state.schema.nodes.accordion)(state.selection) ||
      findParentNode(node => node.type === state.schema.nodes.accordion)(
          state.selection);
};

export const findGrid = state => {
  return findSelectedNodeOfType(state.schema.nodes.grid)(state.selection) ||
      findParentNode(node => node.type === state.schema.nodes.grid)(
          state.selection);
};

export const findColumn = state => {
  return findSelectedNodeOfType(state.schema.nodes.grid_column)(
          state.selection) ||
      findParentNode(node => node.type === state.schema.nodes.grid_column)(
          state.selection);
};

export const findNextColumn = ($cell, dir) => {
  if (dir < 0) {
    let before = $cell.nodeBefore;
    if (before) return $cell.pos - before.nodeSize;
    for (
        let row = $cell.index(-1) - 1, rowEnd = $cell.before();
        row >= 0;
        row--
    ) {
      let rowNode = $cell.node(-1).child(row);
      if (rowNode.childCount) return rowEnd - 1 - rowNode.lastChild.nodeSize;
      rowEnd -= rowNode.nodeSize;
    }
  } else {
    if ($cell.index() < $cell.parent.childCount - 1)
      return $cell.pos + $cell.nodeAfter.nodeSize;
    let grid = $cell.node(-1);
    for (
        let row = $cell.indexAfter(-1), rowStart = $cell.after();
        row < grid.childCount;
        row++
    ) {
      let rowNode = grid.child(row);
      if (rowNode.childCount) return rowStart + 1;
      rowStart += rowNode.nodeSize;
    }
  }
}

export const replaceAndSelectNode = (view, newNode) => {
    const tr = view.state.tr;
    tr.replaceSelectionWith(newNode).scrollIntoView();

    let newPos;
    tr.doc.nodesBetween(view.state.tr.selection.anchor - 1, tr.selection.anchor, (node, pos) => {
        if (!newPos && node.type === newNode.type) {
            newPos = pos;
        }

        return newPos == null;
    });

    view.dispatch(tr.setSelection(NodeSelection.create(tr.doc, newPos)));
}